/**
 * SupportQueueChart - Donut chart showing support queue breakdown
 *
 * Segments:
 * - Open (blue) - New tickets
 * - In Progress (amber) - Being worked on
 * - Resolved (emerald) - Fixed, awaiting closure
 * - Closed (slate) - Completed tickets
 *
 * Center displays total tickets count
 */
import { cn } from '@respectly/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@respectly/ui';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

// ============================================
// Types
// ============================================

interface SupportQueueData {
  open: number;
  inProgress: number;
  resolved: number;
  closed: number;
}

interface SupportQueueChartProps {
  data?: SupportQueueData;
  className?: string;
}

interface ChartDataItem {
  name: string;
  value: number;
  color: string;
}

// ============================================
// Configuration
// ============================================

const COLORS = {
  open: '#3b82f6',
  inProgress: '#f59e0b',
  resolved: '#10b981',
  closed: '#64748b',
};

// ============================================
// Formatters
// ============================================

const numberFormatter = new Intl.NumberFormat('en-US');

function formatNumber(value: number): string {
  return numberFormatter.format(value);
}

// ============================================
// Custom Tooltip
// ============================================

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    payload: ChartDataItem;
  }>;
}

function CustomTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload?.length) return null;

  const entry = payload[0];
  if (!entry) return null;

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-lg">
      <div className="flex items-center gap-2 mb-1">
        <div
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: entry.payload?.color }}
        />
        <span className="text-sm font-medium text-slate-900 dark:text-white">
          {entry.name}
        </span>
      </div>
      <p className="text-lg font-bold tabular-nums text-slate-900 dark:text-white">
        {formatNumber(entry.value)}
      </p>
      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">tickets</p>
    </div>
  );
}

// ============================================
// Custom Legend
// ============================================

interface LegendProps {
  payload?: Array<{
    value: string;
    color: string;
    payload: ChartDataItem;
  }>;
}

function CustomLegend({ payload }: LegendProps) {
  if (!payload) return null;

  return (
    <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-4">
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center gap-1.5">
          <div
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-xs text-slate-600 dark:text-slate-400">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

// ============================================
// Component
// ============================================

export function SupportQueueChart({ data, className }: SupportQueueChartProps) {
  // Prepare chart data - only show non-zero values
  const chartData: ChartDataItem[] = data
    ? [
        { name: 'Open', value: data.open, color: COLORS.open },
        { name: 'In Progress', value: data.inProgress, color: COLORS.inProgress },
        { name: 'Resolved', value: data.resolved, color: COLORS.resolved },
        { name: 'Closed', value: data.closed, color: COLORS.closed },
      ].filter((item) => item.value > 0)
    : [];

  const totalTickets = data
    ? data.open + data.inProgress + data.resolved + data.closed
    : 0;
  const activeTickets = data ? data.open + data.inProgress : 0;

  if (chartData.length === 0) {
    return (
      <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Support Queue</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[280px]">
          <p className="text-sm text-slate-500">No data available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold">Support Queue</CardTitle>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          Ticket status breakdown
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="h-[280px] w-full relative">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="45%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={2}
                dataKey="value"
                stroke="none"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />
            </PieChart>
          </ResponsiveContainer>

          {/* Center label showing total tickets */}
          <div
            className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
            style={{ top: '-20px' }}
          >
            <span className="text-xs text-slate-500 dark:text-slate-400">Total</span>
            <span className="text-2xl font-bold tabular-nums text-slate-900 dark:text-white">
              {formatNumber(totalTickets)}
            </span>
            <span className="text-[10px] text-slate-400 mt-0.5">
              {formatNumber(activeTickets)} active
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// Skeleton
// ============================================

export function SupportQueueChartSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
      <CardHeader className="pb-2">
        <div className="h-5 w-32 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
        <div className="h-3 w-40 bg-slate-200 dark:bg-slate-700 rounded animate-pulse mt-1" />
      </CardHeader>
      <CardContent>
        <div className="h-[280px] flex items-center justify-center">
          <div className="h-44 w-44 rounded-full border-8 border-slate-200 dark:border-slate-700 animate-pulse" />
        </div>
      </CardContent>
    </Card>
  );
}
